/**
 * CreatePlaylistModal Component
 * Modal for creating a new playlist - AI-generated, manual, or imported from IMDB CSV
 */

import { useRef, useState } from 'react';
import { createPlaylist, importPlaylistFromCsv, ValidationError, type Playlist } from '../api';
import ModalPortal from './ModalPortal';

type CreationMode = 'ai' | 'manual' | 'import';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (playlist: Playlist) => void;
}

export default function CreatePlaylistModal({ isOpen, onClose, onCreated }: Props) {
  const [mode, setMode] = useState<CreationMode>('ai');
  const [prompt, setPrompt] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[] | null>(null);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [markAsWatched, setMarkAsWatched] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCsvFile(file);
      // Auto-fill name from filename if empty
      if (!name.trim()) {
        const fileName = file.name.replace(/\.csv$/i, '');
        setName(fileName);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Please provide a name for your playlist');
      return;
    }

    if (mode === 'ai' && !prompt.trim()) {
      setError('Please describe what kind of movies you want');
      return;
    }

    if (mode === 'import' && !csvFile) {
      setError('Please select a CSV file to import');
      return;
    }

    setLoading(true);
    setError(null);
    setSuggestions(null);

    try {
      let playlist: Playlist;

      if (mode === 'import' && csvFile) {
        // Read the CSV file content
        const csvContent = await csvFile.text();
        playlist = await importPlaylistFromCsv({
          name: name.trim(),
          csvContent,
          markAsWatched: markAsWatched || undefined,
        });
      } else {
        playlist = await createPlaylist({
          prompt: mode === 'ai' ? prompt.trim() : undefined,
          name: name.trim() || undefined,
        });
      }

      // Reset form state
      setPrompt('');
      setName('');
      setMode('ai');
      setCsvFile(null);
      setMarkAsWatched(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      onCreated(playlist);
      onClose();
    } catch (err) {
      if (err instanceof ValidationError) {
        setError(err.message);
        setSuggestions(err.suggestions || null);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to create playlist');
        setSuggestions(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setPrompt('');
      setName('');
      setMode('ai');
      setCsvFile(null);
      setMarkAsWatched(false);
      setError(null);
      setSuggestions(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onClose();
    }
  };

  const isSubmitDisabled =
    loading || !name.trim() || (mode === 'ai' && !prompt.trim()) || (mode === 'import' && !csvFile);

  return (
    <ModalPortal>
      <div className="modal-overlay" onClick={handleClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Create New Playlist</h2>
            <button className="modal-close" onClick={handleClose} disabled={loading}>
              &times;
            </button>
          </div>

          <div className="mode-toggle">
            <button
              type="button"
              className={`mode-toggle-btn ${mode === 'ai' ? 'active' : ''}`}
              onClick={() => setMode('ai')}
              disabled={loading}
            >
              AI-Generated
            </button>
            <button
              type="button"
              className={`mode-toggle-btn ${mode === 'manual' ? 'active' : ''}`}
              onClick={() => setMode('manual')}
              disabled={loading}
            >
              Manual
            </button>
            <button
              type="button"
              className={`mode-toggle-btn ${mode === 'import' ? 'active' : ''}`}
              onClick={() => setMode('import')}
              disabled={loading}
            >
              Import
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            {mode === 'import' && (
              <div className="form-group">
                <label htmlFor="csv-file">
                  CSV File <span className="required">*</span>
                </label>
                <input
                  id="csv-file"
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  disabled={loading}
                  className="file-input"
                />
                <p className="form-hint">
                  Upload a CSV file with movie data. Supports IMDB exports, or any CSV with movie
                  titles, IMDB IDs, or IMDB links.
                </p>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="playlist-name">
                Playlist Name <span className="required">*</span>
              </label>
              <input
                id="playlist-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., My Favorite Spy Movies"
                disabled={loading}
                required
              />
            </div>

            {mode === 'ai' && (
              <div className="form-group">
                <label htmlFor="playlist-prompt">
                  Describe the movies you want <span className="required">*</span>
                </label>
                <textarea
                  id="playlist-prompt"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="e.g., Movies about the Cold War that focus on spies. Include non-English language films."
                  rows={4}
                  disabled={loading}
                  required
                />
                <p className="form-hint">
                  Be as specific as you like. You can mention genres, actors, directors, time
                  periods, themes, or any other criteria.
                </p>
              </div>
            )}

            {mode === 'manual' && (
              <p className="form-hint" style={{ marginBottom: '1rem' }}>
                Create an empty playlist and add movies manually using the search feature.
              </p>
            )}

            {mode === 'import' && (
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={markAsWatched}
                    onChange={(e) => setMarkAsWatched(e.target.checked)}
                    disabled={loading}
                  />
                  <span>Mark all imported movies as watched</span>
                </label>
              </div>
            )}

            {mode === 'import' && csvFile && (
              <p className="form-hint" style={{ marginBottom: '1rem' }}>
                Selected: <strong>{csvFile.name}</strong>
              </p>
            )}

            {error && (
              <div className="form-error">
                <p>{error}</p>
                {suggestions && suggestions.length > 0 && (
                  <div className="validation-suggestions">
                    <p>Try something like:</p>
                    <ul>
                      {suggestions.map((suggestion, index) => (
                        <li key={index}>
                          <button
                            type="button"
                            className="suggestion-btn"
                            onClick={() => {
                              setPrompt(suggestion);
                              setError(null);
                              setSuggestions(null);
                            }}
                          >
                            {suggestion}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div className="modal-actions">
              <button type="button" className="secondary" onClick={handleClose} disabled={loading}>
                Cancel
              </button>
              <button type="submit" disabled={isSubmitDisabled}>
                {loading
                  ? mode === 'import'
                    ? 'Importing...'
                    : 'Creating...'
                  : mode === 'import'
                    ? 'Import Playlist'
                    : 'Create Playlist'}
              </button>
            </div>
          </form>

          {mode === 'ai' && (
            <div className="modal-examples">
              <p>Examples:</p>
              <ul>
                <li>"James Bond movies"</li>
                <li>"All Marvel movies in order"</li>
                <li>"Movies with Tom Cruise"</li>
                <li>"Movies about the 2008 financial crisis"</li>
                <li>"Classic French New Wave cinema"</li>
              </ul>
            </div>
          )}

          {mode === 'import' && (
            <div className="modal-examples">
              <p>Supported CSV formats:</p>
              <ul>
                <li>IMDB exports (with "Const" or IMDB link columns)</li>
                <li>Any CSV with "Title" and "Year" columns</li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </ModalPortal>
  );
}
